import { cloneDeep } from "lodash";
import React, { useEffect } from "react";
import styled from "styled-components";

import { Amount } from "../../../../shared/core/amount/amount";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { attachmentsManager, spendingsManager } from "../../../../shared/core/service/services";
import type { Account } from "../../../../shared/domains/account/account";
import { AccountingEntryType } from "../../../../shared/domains/accounting-transaction/accounting-entry";
import { AccountingTransactionType } from "../../../../shared/domains/accounting-transaction/accounting-transaction-type";
import type { Attachment } from "../../../../shared/domains/accounting-transaction/transaction/attachment";
import { AttachmentsLinks } from "../../../../shared/domains/accounting-transaction/transaction/attachments-links";
import { SpendingLinks } from "../../../../shared/domains/accounting-transaction/transaction/spending-links";
import type { AccountingTransaction } from "../../../../shared/domains/accounting-transaction/transaction/transaction";
import { TransactionLinks } from "../../../../shared/domains/accounting-transaction/transaction/transaction-links";
import { useAttachments } from "../../../../shared/domains/accounting-transaction/transaction/use-attachments";
import { useSpendings } from "../../../../shared/domains/accounting-transaction/transaction/use-spendings";
import { useTransaction } from "../../../../shared/domains/accounting-transaction/transaction/use-transaction";
import { useAttachmentLinks } from "../../../../shared/domains/accounting-transaction/use-attachment-links";
import { useTransactionLinks } from "../../../../shared/domains/accounting-transaction/use-transaction-links";
import type { AuthorizationHold } from "../../../../shared/domains/authorization-hold/authorization-hold";
import { useAuthorizationHolds } from "../../../../shared/domains/authorization-hold/use-authorization-hold";
import { getUrlFromLink } from "../../../../shared/domains/BaseUrl";
import {
  ADD_OR_EDIT_SPENDING,
  REMOVE_ATTACHMENT_MODAL_ID,
  REMOVE_SPENDING_MODAL_ID,
} from "../../../core/modal/modal-id";
import { useRTL } from "../../../domain/language/use-rtl";
import { WebImagesConverter } from "../../../utils/web-images-converter";
import AddButtonInputFile from "../../common/add-button-input-file";
import AddButton from "../../common/buttons/add-button";
import { ErrorMessage } from "../../common/error-message";
import { Modal } from "../../common/modal/modal";
import { PageBackLink } from "../../common/page/page-back-header";
import { MainColorSpinner } from "../../common/spinner";
import { StaticAssets } from "../../styles/custom-assets";
import { shadows, theme } from "../../styles/theme";
import { TransactionStateTag } from "../account-transactions/components/transaction-state-tag";
import { AddSpendingModal } from "./components/add-spending-modal";
import AttachmentPreview from "./components/attachment-preview";
import EmptyZone from "./components/empty-zone";
import JustificationCard from "./components/justification-card";
import { RemoveConfirmModal } from "./components/remove-confirm-modal";
import type { BlankSpending } from "./components/spending-form";
import SpendingForm from "./components/spending-form";
import SpendingPreview from "./components/spending-preview";

const getInitialSpending = (transaction?: AccountingTransaction | AuthorizationHold): BlankSpending => ({
  category: null,
  subcategory: null,
  label: "",
  amount: new Amount(Number(transaction?.amount?.value) / 100, transaction?.amount?.currency || "EUR"),
  additionalData: [],
});

export default function JustifyTransactionScreen(props) {
  const { formatMessage } = useIntl();
  const { location, history } = props;
  const params = location.state;
  const { isRTL } = useRTL();
  const [attachmentError, setAttachmentError] = React.useState<string>("");
  const { canGetContent } = useAttachmentLinks();
  const { transaction: regularTransaction, refresh: refreshTransaction } = useTransaction(
    params.account.id,
    params.transaction.id,
  );
  const [blankSpending, setBlankSpending] = React.useState<BlankSpending>();

  const { data: authorizationHolds, refresh: pendingTransactionRefresh } = useAuthorizationHolds({
    id: params.account.id,
  } as Account);
  const authHold = authorizationHolds.find((authHold) => authHold.id === params.transaction.id);

  const isPendingTransaction = params.transaction.objectType === AccountingTransactionType.AuthorizationHold;

  const [isLoading, setIsLoading] = React.useState<{
    attachment: boolean;
    spending: boolean;
  }>({
    attachment: false,
    spending: false,
  });

  const { canJustify, canAddAttachment, canAddSpending, isOutgoing } = useTransactionLinks();

  const transaction = isPendingTransaction ? authHold : regularTransaction;
  // The header names the expense the way the list and the details modal do. A pending hold carries
  // none of those fields, so the screen's own title stands in.
  const expense = transaction as AccountingTransaction | undefined;
  const transactionDescription =
    expense?.shortDescription ||
    expense?.detailedAccounting?.find((accounting) => accounting.type === AccountingEntryType.Transfer)?.description ||
    expense?.type?.label ||
    formatMessage("justifyScreen.title");

  const { spendings, refresh: refreshSpendings, loading: spendingsLoading } = useSpendings(transaction);
  const { attachments, loading: documentsLoading } = useAttachments(transaction);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (transaction && file) {
      const links = transaction.links;
      const fileConverter = new WebImagesConverter();
      const convertedFile = await fileConverter.fileIntoBase64(file);
      setIsLoading({
        ...isLoading,
        attachment: true,
      });
      try {
        setAttachmentError("");
        const url = getUrlFromLink(links, TransactionLinks.AddAttachment);
        await attachmentsManager.addAttachment(transaction.id, convertedFile.split(",")[1], "", url);
        isPendingTransaction ? pendingTransactionRefresh() : refreshTransaction();
      } catch (e) {
        console.log(e);
        setAttachmentError(e.response.data.error.message);
      } finally {
        setIsLoading({
          ...isLoading,
          attachment: false,
        });
      }
    }
  };

  const handleOpenFile = async (attachment: Attachment) => {
    if (transaction && canGetContent(attachment)) {
      setIsLoading({
        ...isLoading,
        attachment: true,
      });
      try {
        const url = getUrlFromLink(attachment.links, AttachmentsLinks.GetContent);
        const attachmentData = await attachmentsManager.getContent(transaction.id, attachment.id, url);
        if (attachmentData) {
          const file = new Blob([attachmentData.content], { type: attachmentData.contentType });
          // const extension = mime.extension(attachmentData.contentType);
          const fileURL = URL.createObjectURL(file);
          const link = document.createElement("a");
          link.href = fileURL;
          link.target = "_blank";
          // link.download = `Attachment_${attachment.id}.${extension}`;
          link.click();
        }
      } catch (e) {
        setAttachmentError(e.response.data.error.message);
        console.log(e);
      } finally {
        setIsLoading({
          ...isLoading,
          attachment: false,
        });
      }
    }
  };

  const handleAddNewSpending = () => {
    setBlankSpending(getInitialSpending(transaction));
  };

  const handleEditSpending = (spending) => {
    setBlankSpending({
      ...cloneDeep(spending),
    });
  };

  const handleSpendingSuccess = () => {
    isPendingTransaction ? pendingTransactionRefresh() : refreshTransaction();
    refreshSpendings();
    Modal.dismiss(ADD_OR_EDIT_SPENDING);
  };

  useEffect(() => {
    if (blankSpending && transaction) {
      Modal.present(
        ADD_OR_EDIT_SPENDING,
        () => (
          <AddSpendingModal>
            <SpendingForm transaction={transaction} spending={blankSpending} onSuccess={handleSpendingSuccess} />
          </AddSpendingModal>
        ),
        {},
      );
    }
  }, [blankSpending]);

  const handleFileDelete = async (attachment: Attachment) => {
    Modal.present(
      REMOVE_ATTACHMENT_MODAL_ID,
      () => (
        <RemoveConfirmModal
          modalId={REMOVE_ATTACHMENT_MODAL_ID}
          labels="justifyScreen.attachments.deletePrompt"
          onConfirm={() => handleConfirmFileDelete(attachment)}
        />
      ),
      {
        canBeDismissed: true,
      },
    );
  };

  const handleConfirmFileDelete = async (attachment: Attachment) => {
    if (transaction) {
      setIsLoading({
        ...isLoading,
        attachment: true,
      });
      try {
        setAttachmentError("");
        const url = getUrlFromLink(attachment.links, AttachmentsLinks.Delete);
        await attachmentsManager.deleteAttachment(transaction.id, attachment.id, url);
      } catch (e) {
        console.log(e);
        setAttachmentError(e.response.data.error.message);
      } finally {
        setIsLoading({
          ...isLoading,
          attachment: false,
        });
      }
    }
  };

  const handleSpendingDelete = async (attachment: Attachment) => {
    Modal.present(
      REMOVE_SPENDING_MODAL_ID,
      () => (
        <RemoveConfirmModal
          modalId={REMOVE_SPENDING_MODAL_ID}
          labels="justifyScreen.spendings.deletePrompt"
          onConfirm={() => handleConfirmSpendingDelete(attachment)}
        />
      ),
      {
        canBeDismissed: true,
      },
    );
  };

  const handleConfirmSpendingDelete = async (spending: Attachment) => {
    if (transaction) {
      setIsLoading({
        ...isLoading,
        spending: true,
      });
      try {
        setAttachmentError("");
        const url = getUrlFromLink(spending.links, SpendingLinks.Delete);
        await spendingsManager.deleteSpendingDetails(transaction.id, spending.id, url);
      } catch (e) {
        console.log(e);
        setAttachmentError(e.response.data.error.message);
      } finally {
        setIsLoading({
          ...isLoading,
          spending: false,
        });
      }
    }
  };

  return (
    <PageWrapper isLoading={isLoading.attachment || isLoading.spending}>
      <PageBackLink onBack={() => history.goBack()} />
      <Header>
        <Title>{transactionDescription}</Title>
        {!!transaction && isOutgoing(transaction) && (
          <Tags>
            <TransactionStateTag
              label={formatMessage(
                transaction.justified ? "accountTransactions.tagJustified" : "accountTransactions.tagNotJustified",
              )}
              done={!!transaction.justified}
            />
            <TransactionStateTag
              label={formatMessage(
                transaction.qualified ? "accountTransactions.tagQualified" : "accountTransactions.tagNotQualified",
              )}
              done={!!transaction.qualified}
            />
          </Tags>
        )}
      </Header>
      {!!transaction?.id && (
        <Reference>{formatMessage("accountingTransactionDetailsModal.transaction", { id: transaction.id })}</Reference>
      )}
      <SectionTitle>{formatMessage("justifyScreen.attachments.title")}</SectionTitle>
      {!!attachmentError && <StyledErrorMessage>{attachmentError}</StyledErrorMessage>}
      {isLoading.attachment && <StyledMainColorSpinner />}
      {documentsLoading ? (
        <StyledMainColorSpinner />
      ) : !attachments.length ? (
        transaction &&
        canAddAttachment(transaction) && (
          <EmptyZone
            isDropZone
            disabled={documentsLoading}
            onChange={handleFileChange}
            title={formatMessage("justifyScreen.attachments.sectionTitle")}
            subtitle={formatMessage("justifyScreen.attachments.sectionSubtitle")}
            icon={<ZoneIllustration src={StaticAssets.justifyTransaction} alt="" />}
          />
        )
      ) : (
        <>
          {attachments.length > 0 && (
            <List>
              {attachments.map((attachment, index) => (
                <JustificationCard
                  key={index}
                  isRTL={isRTL}
                  onOpen={() => handleOpenFile(attachment)}
                  onDelete={() => handleFileDelete(attachment)}
                >
                  <AttachmentPreview attachment={attachment} transaction={transaction} />
                </JustificationCard>
              ))}
            </List>
          )}
          {transaction && canAddAttachment(transaction) && (
            <AddButtonInputFile onChange={handleFileChange} disabled={documentsLoading}>
              {formatMessage(
                attachments.length === 0
                  ? "justifyScreen.attachments.addButtonLabel"
                  : "justifyScreen.attachments.addOtherButtonLabel",
              )}
            </AddButtonInputFile>
          )}
        </>
      )}
      {((transaction?.locked && spendings.length > 0) || !transaction?.locked) && (
        <>
          <SectionTitle>{formatMessage("justifyScreen.spendings.title")}</SectionTitle>
          {/* {!!spendingError && <StyledErrorMessage>{spendingError}</StyledErrorMessage>} */}
          {isLoading.spending && <StyledMainColorSpinner />}
          {spendingsLoading ? (
            <StyledMainColorSpinner />
          ) : !spendings.length &&
            transaction &&
            !transaction.locked &&
            canJustify(transaction) &&
            canAddSpending(transaction) ? (
            <EmptyZone
              title={formatMessage("justifyScreen.spendings.sectionTitle")}
              subtitle={formatMessage("justifyScreen.spendings.sectionSubtitle")}
              disabled={spendingsLoading}
              onClick={handleAddNewSpending}
              icon={<ZoneIllustration src={StaticAssets.qualifyTransaction} alt="" />}
            />
          ) : (
            <>
              {spendings.length > 0 && (
                <List>
                  {spendings.map((spending, index) => (
                    <JustificationCard
                      variant="spending"
                      key={index}
                      isRTL={isRTL}
                      onDelete={transaction?.locked ? undefined : () => handleSpendingDelete(spending)}
                      onOpen={transaction?.locked ? undefined : () => handleEditSpending(spending)}
                    >
                      <SpendingPreview spending={spending} />
                    </JustificationCard>
                  ))}
                </List>
              )}
              {transaction && !transaction.locked && canJustify(transaction) && canAddSpending(transaction) && (
                <AddButton disabled={spendingsLoading} onClick={handleAddNewSpending}>
                  {formatMessage(
                    attachments.length === 0
                      ? "justifyScreen.spendings.addButtonLabel"
                      : "justifyScreen.spendings.addOtherButtonLabel",
                  )}
                </AddButton>
              )}
            </>
          )}
        </>
      )}
    </PageWrapper>
  );
}

const PageWrapper = styled.div<{ isLoading: boolean }>`
  width: 100%;
  ${({ isLoading }) => {
    if (isLoading) {
      return `
				pointer-events: none;
				opacity: 0.5;
			`;
    }
    return "";
  }}
`;

/* The rows sit inside one panel, the way the reworked lists elsewhere are drawn. */
const List = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  overflow: hidden;
  margin-bottom: 16px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;
  ${shadows.sm};
`;

const ZoneIllustration = styled.img`
  width: 67px;
  height: 68px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h1`
  ${theme.boldText};
  margin: 0;
  flex: 1 1 auto;

  /* A label carrying no space still has to give way, or it pushes the tags out. */
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const Tags = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  gap: 8px;
`;

const Reference = styled.span`
  ${theme.text};
  display: block;
  margin-top: 4px;
  font-size: 13px;
  line-height: 18px;
  color: ${theme.colors.gray[500]};
`;

const SectionTitle = styled.h2`
  ${theme.mediumText};
  margin: 28px 0 12px;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  color: ${theme.colors.gray[800]};
`;

const StyledMainColorSpinner = styled(MainColorSpinner)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin-bottom: 16px;
`;
